import type { ILead } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import { getTimezoneAbbr, parseDateTimeInUserTimezone } from 'src/utils/format-date-time';
import { getDisplayedSmartFields } from 'src/components/lead-sidebar/displayed-smart-fields';

type Params = {
    lead: ILead;
    /** Display name for the lead, e.g. from LeadUtils.getLeadName */
    leadName: string;
    /** The smart fields shown in the Lead Info section (LeadSidebar's filtered list). */
    validSmartFields?: [string, string | Record<string, string>][];
    propertyName?: string;
    upcomingShowing?: IShowing;
    timezoneOverride?: string | null;
};

/**
 * Format lead details as plain text for sharing (e.g. copying to clipboard).
 * Sections with no data are omitted.
 */
export const formatLeadShareText = ({
    lead,
    leadName,
    validSmartFields = [],
    propertyName,
    upcomingShowing,
    timezoneOverride,
}: Params): string => {
    const sections: string[] = [`Lead Name: ${leadName}`];

    const contactLines: string[] = [];
    if (lead.email) {
        contactLines.push(`* email: ${lead.email}`);
    }
    if (lead.phone_number) {
        contactLines.push(`* phone: ${lead.phone_number}`);
    }
    if (contactLines.length > 0) {
        sections.push(['Contact Information', ...contactLines].join('\n'));
    }

    if (propertyName) {
        sections.push(`Property Interest\n${propertyName}`);
    }

    const smartFieldLines = getDisplayedSmartFields(validSmartFields).map(
        ({ label, value }) => `* ${label}: ${value}`
    );
    if (smartFieldLines.length > 0) {
        sections.push(['Lead Info', ...smartFieldLines].join('\n'));
    }

    const notes = lead.notes?.contents?.trim();
    if (notes) {
        sections.push(`Notes\n${notes}`);
    }

    if (upcomingShowing) {
        const showingDate = parseDateTimeInUserTimezone(upcomingShowing.start_time, timezoneOverride);
        const timezone = timezoneOverride ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
        const tzAbbr = timezone ? ` ${getTimezoneAbbr(timezone)}` : '';
        sections.push(`Tour Info\nTime: ${showingDate.format('ddd, MMM D, YYYY')} at ${showingDate.format('h:mm A')}${tzAbbr}`);
    }

    return sections.join('\n\n');
};
